import React from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";

const Writing = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [message, setMessage] = React.useState<{
    text: string;
    type: "success" | "error";
  } | null>(null);
  const [showSubmit, setShowSubmit] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState<string>("");
  const questionId = localStorage.getItem("questionId");

  const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setMessage(null);
    setShowSubmit(false);
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await fetch(`${API_BASE_URL}/upload/image`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      setImageUrl(data.imageUrl);
      setMessage({ text: "Image uploaded successfully!", type: "success" });
      setShowSubmit(true);
      console.log("Image uploaded successfully:", data);
    } catch (error) {
      setMessage({
        text: "Failed to upload image. Please try again.",
        type: "error",
      });
      console.error("Error uploading image:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/screening-questions/${questionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            writing: {
              answer: imageUrl,
              marks: 0,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit");
      }

      setMessage({ text: "Submitted successfully!", type: "success" });
      navigate("/completion");
    } catch (error) {
      setMessage({
        text: "Failed to submit. Please try again.",
        type: "error",
      });
      console.error("Error submitting:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-200 justify-center items-center px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <div className="bg-white p-8 rounded-3xl shadow-lg mb-6 overflow-hidden relative">
          <div className="bg-red-50 h-40 rounded-full w-40 -mr-12 -mt-12 absolute opacity-70 right-0 top-0"></div>
          <div className="bg-red-50 h-32 rounded-full w-32 -mb-8 -ml-8 absolute bottom-0 left-0 opacity-70"></div>
          <div className="relative z-10">
            <h1 className="bg-clip-text bg-gradient-to-r text-4xl text-transparent font-bold from-red-600 mb-5 to-pink-600">
              Writing Test
            </h1>
            <div className="bg-gradient-to-r h-1.5 rounded-full w-24 from-red-500 mb-6 to-pink-500"></div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-lg">
          <h2 className="text-base sm:text-lg font-semibold mb-4 sm:mb-8 text-red-800  pl-3">
            1) Write the same form of letters in as in the example.
          </h2>

          <div className="flex flex-col sm:flex-row items-center mb-6 bg-white p-4 rounded-lg shadow-sm">
            <h1 className="text-xl font-bold text-center text-red-700 mr-4">
              EX: か （か）
            </h1>
            <div className="mt-3 sm:mt-0 flex flex-col items-start gap-2">
              <label className="inline-block px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 cursor-pointer transition duration-300">
                {isLoading ? (
                  <div className="flex items-center">
                    <svg
                      className="animate-spin h-5 w-5 mr-2"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Uploading...
                  </div>
                ) : (
                  "Upload Image"
                )}
                <input
                  type="file"
                  id="myFile"
                  accept="image/*"
                  name="writing_image"
                  onChange={uploadImage}
                  className="hidden"
                  disabled={isLoading}
                />
              </label>
              {message && (
                <div
                  className={`mt-2 px-4 py-2 rounded ${
                    message.type === "success"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {message.text}
                </div>
              )}
            </div>
          </div>

          <div className="p-4 bg-white rounded-lg shadow-sm">
            <table className="w-full border-collapse">
              <tbody>
                {/* Row 1 */}
                <tr>
                  <td className="p-3 text-2xl font-bold text-center border border-red-200 rounded-lg m-1 bg-red-50 hover:bg-red-100 transition duration-300">
                    (1) を
                  </td>
                  <td className="p-3 text-2xl font-bold text-center border border-red-200 rounded-lg m-1 bg-red-50 hover:bg-red-100 transition duration-300">
                    (2) ね
                  </td>
                  <td className="p-3 text-2xl font-bold text-center border border-red-200 rounded-lg m-1 bg-red-50 hover:bg-red-100 transition duration-300">
                    (3) む
                  </td>
                  <td className="p-3 text-2xl font-bold text-center border border-red-200 rounded-lg m-1 bg-red-50 hover:bg-red-100 transition duration-300">
                    (4) セ
                  </td>
                  <td className="p-3 text-2xl font-bold text-center border border-red-200 rounded-lg m-1 bg-red-50 hover:bg-red-100 transition duration-300">
                    (5) ツ
                  </td>
                </tr>
                {/* Row 2 */}
                <tr>
                  <td className="p-3 text-2xl font-bold text-center border border-red-200 rounded-lg m-1 bg-red-50 hover:bg-red-100 transition duration-300">
                    (6) 東
                  </td>
                  <td className="p-3 text-2xl font-bold text-center border border-red-200 rounded-lg m-1 bg-red-50 hover:bg-red-100 transition duration-300">
                    (7) 来
                  </td>
                  <td className="p-3 text-2xl font-bold text-center border border-red-200 rounded-lg m-1 bg-red-50 hover:bg-red-100 transition duration-300">
                    (8) 学
                  </td>
                  <td className="p-3 text-2xl font-bold text-center border border-red-200 rounded-lg m-1 bg-red-50 hover:bg-red-100 transition duration-300">
                    (9) 間
                  </td>
                  <td className="p-3 text-2xl font-bold text-center border border-red-200 rounded-lg m-1 bg-red-50 hover:bg-red-100 transition duration-300">
                    (10) 高
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {showSubmit && (
            <div className="mt-6 flex justify-center">
              <button
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-300"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Writing;
